import React from "react";
import { CustomThemeKeys } from "../../theme";
import { formatMoney } from "../../utils/string";
import { UserRetailerShoppingCart } from "@/type";
import { appThemeQueryOptions } from "@/api/cache/appThemeQueryOptions";
import { useQuery } from "@tanstack/react-query";
import { useAllCarts } from "@/api/cache/cartQueryOptions";

export interface AddToBagButtonProps {
  disabled?: boolean;
  children?: React.ReactNode;

  onClick?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    checkoutData: {
      allCarts: UserRetailerShoppingCart[];
      retailerIds: number[];
    }
  ) => void;
  price?: number;
  textColor?: string;
  bgColor?: string;
}

const AddToBagButton: React.FC<AddToBagButtonProps> = ({
  disabled,
  onClick,
  price,
  children,
  textColor,
  bgColor,
}) => {
  const { data: styleData } = useQuery(appThemeQueryOptions());
  const { data: allCarts } = useAllCarts({
    select: (data) => data.userRetailerShoppingCarts,
    // only read from cache to avoid resetting updated local carts
    enabled: false, // Prevents fetching
    staleTime: Infinity, // Prevents background refetches
  });
  const retailerIds = allCarts?.map((cart) => cart.retailerId);
  const globalButtonRadius = styleData?.globalButtonRadius;

  const customTheme =
    styleData?.componentStyleOverrides?.[CustomThemeKeys.ADD_TO_BAG_BUTTON];

  const buttonText = customTheme?.customText || "Add to bag";
  const backgroundColor = bgColor
    ? bgColor
    : customTheme?.enabled
    ? customTheme?.secondary
    : styleData?.secondary;
  const color = textColor
    ? textColor
    : customTheme?.enabled
    ? customTheme?.primary
    : styleData?.primary;
  const cap =
    customTheme?.enabled && customTheme?.capitalize ? "uppercase" : "none";

  const addToBagButtonOutlineColor =
    customTheme?.enabled && customTheme?.borderEnabled
      ? customTheme?.tertiary
      : undefined;

  return (
    <>
      <button
        disabled={disabled}
        className={`bc-h-[46px] desktop:bc-h-[52px] bc-cursor-pointer bc-w-full desktop:bc-w-fit bc-box-border bc-disabled:cursor-not-allowed bc-medium-adjust-text bc-font-medium bc-px-[12px] desktop:bc-px-[20px] bc-whitespace-nowrap`}
        onClick={(e) => {
          if (onClick && allCarts && retailerIds) {
            onClick(e, { allCarts, retailerIds });
          }
        }}
        style={{
          backgroundColor: backgroundColor,
          color: color,
          border: addToBagButtonOutlineColor
            ? `1px solid ${addToBagButtonOutlineColor}`
            : "none",
          textTransform: cap,
          borderRadius: globalButtonRadius,
        }}
      >
        {children ? (
          children
        ) : (
          <div className="bc-flex bc-justify-center bc-items-center">
            {buttonText}
            <div
              className="!bc-flex bc-h-[6px] bc-w-[6px] bc-ml-[6px] bc-mr-[6px] bc-rounded-full"
              style={{
                backgroundColor: color,
              }}
            />
            {price ? formatMoney(price) : ""}
          </div>
        )}
      </button>
    </>
  );
};

export default AddToBagButton;
